import React, { useState, useEffect } from 'react';
import { I18n } from 'react-redux-i18n';
import styled from 'styled-components';
import get from 'lodash/get';
import map from 'lodash/map';
import join from 'lodash/join';

import Card from 'components/Card';
import CardHeader from 'components/CardHeader';
import BarGraph from 'components/Charts/BarGraph';
import Spinner from 'components/Spinner';
import ExportButton from 'components/Export';
import EvolutionIcon from 'components/EvolutionIcon';
import ChartInputsBloc from '../components/ChartInputsBloc';
import ChartCompareBloc from '../components/ChartCompareBloc';
import PriceWineCard from '../components/PriceWineCard';

import { useDistributionPrice } from 'apiTyped/hooks/distribution.query';
import { formatParam } from 'utils/formatParam';

const PriceContainer = styled.div`
  display: grid;
  grid-template-columns: calc(50% - 10px) calc(50% - 10px);
  grid-gap: 20px;

  @media (max-width: 1300px) {
    grid-template-columns: 1fr;
  }

  .rightComponentsContainer {
    display: flex;
    flex-direction: row;
    width: 100%;
    align-items: center;

    > div:not(:last-child) {
      margin-right: 10px;
    }
  }

  .headerInputsContainer {
    margin-left: auto;
    display: flex;
    flex-direction: row;
    align-items: center;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
  }

  .headerInputsContainer > *:not(:last-child) {
    margin-right: 10px;
  }

  .rhcTooltipContainer {
    min-width: 350px;
    display: flex;
    flex-direction: row;
    align-items: center;
    font-family: ${(props) => props.theme.fonts.sfcompact};
    font-size: 12px;
    width: 100%;
    margin-bottom: 10px;

    > p {
      color: #abb5be;
      font-family: ${(props) => props.theme.fonts.sfcompact_text};
      font-weight: 600;
    }
  }
  .rhcTooltipValues {
    margin-left: auto;
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 75%;

    p {
      margin-left: 5px;
    }
  }
  .rhcTooltipValues > * {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 33%;
    text-align: center;
  }
  .rhcTooltipValues > div:not(:last-child) {
    margin-right: 15px;
  }

  .rhcTooltip {
    padding: 12px;
    min-width: 250px;
  }
  .rhcDate {
    color: #abb5be;
    text-transform: uppercase;
  }
  .rhcLabel {
    text-transform: uppercase;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    font-weight: 600;
  }

  @media (max-width: ${(props) =>
      props.theme.devices.isTablet ? '0px' : '845px'}) {
    .rightComponentsContainer {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      margin-top: 10px !important;
    }
  }

  .selectSetsContainer {
    margin-bottom: 50px;
  }
`;

const Price = ({ form, isBranded, client_marques, client_wines }) => {
  const [state, setState] = useState({
    activeRadio: 'none',
    wine: null,
    vintageStart: null,
    vintageEnd: null,
    competitorName: null
  });

  // Use the new hook to fetch price data

  const campaign = form.currentCampaign;
  const { isLoading, data } = useDistributionPrice(campaign?.id, form.wineType);

  const { activeRadio, wine, vintageStart, vintageEnd, competitorName } = state;

  // Set first wine when data becomes available
  useEffect(() => {
    if (data?.client_wines?.length > 0 && !wine) {
      setFirstWineInState();
    }
  }, [data?.client_wines?.length, wine]);

  const setFirstWineInState = () => {
    if (isBranded) {
      const wineOptions = getWineOptionsFromBrands();
      const wineData = [...(data?.client_wines || [])].filter(
        (item) => wineOptions.includes(item.wine) && item.vintage
      );
      setState((prev) => ({
        ...prev,
        wine: {
          label: get(wineData, '[0].wine'),
          value: get(wineData, '[0].wine')
        }
      }));
    } else {
      if (form.wine && form.wine.length > 0) {
        setState((prev) => ({
          ...prev,
          wine: {
            label: get(form, 'wine[0].label'),
            value: get(form, 'wine[0].label')
          }
        }));
      } else {
        setState((prev) => ({
          ...prev,
          wine: {
            label: get(data, 'client_wines[0].wine'),
            value: get(data, 'client_wines[0].wine')
          }
        }));
      }
    }
  };

  const getWineOptionsFromBrands = () => {
    const { client_wines } = data || {};
    if (!client_wines || !client_wines.length > 0) {
      return [];
    }
    const { wine } = form;
    if (!wine || !client_marques) {
      return [];
    }
    const winelabels = wine.map((item) => item.label);
    const wineOptions = [];
    client_marques.forEach((marque) => {
      if (winelabels.includes(marque.marque_name)) {
        wineOptions.push(...marque.wines);
      }
    });
    return client_wines
      .filter((item) => wineOptions.includes(item.wine) && item.vintage)
      .map((item) => item.wine);
  };

  const handleChangeChart = (field, value) => {
    if (field === 'wine') {
      setState((prev) => ({
        ...prev,
        activeRadio: 'none',
        competitorName: null,
        vintageStart: null,
        vintageEnd: null
      }));
    } else if (field === 'competitorName') {
      setState((prev) => ({ ...prev, activeRadio: 'rival' }));
    }
    setState((prev) => ({ ...prev, [field]: value }));
  };

  const filterDataSet = (dataSet, selectedWine) => {
    return [...dataSet].filter(
      (item) =>
        item.format === '750' &&
        item.median_price &&
        (!vintageStart ||
          item.vintage === 'NV' ||
          item.vintage === 'nv' ||
          !vintageStart.value ||
          item.vintage >= vintageStart.value) &&
        (!vintageEnd ||
          item.vintage === 'NV' ||
          item.vintage === 'nv' ||
          !vintageEnd.value ||
          item.vintage <= vintageEnd.value) &&
        selectedWine &&
        item.wine === selectedWine.label
    );
  };

  const getVintageOptions = () => {
    const clientWines = data?.client_wines || [];
    return [...new Set(clientWines.map((item) => item.vintage))]
      .sort()
      .reverse()
      .map((item) => ({ label: item, value: item }));
  };

  const formatVintageChart = () => {
    const clientWines = data?.client_wines || [];

    let formattedData = filterDataSet(clientWines, wine);

    if (activeRadio === 'year') {
      const client_wines_ly = data?.client_wines_ly || [];
      const data_ly = filterDataSet(client_wines_ly, wine);
      formattedData = [formattedData, data_ly];
    } else if (competitorName && activeRadio === 'rival') {
      const rival_wines = data?.rival_wines || [];
      const competitorData = filterDataSet(rival_wines, competitorName);

      formattedData = [formattedData, competitorData];
    } else {
      formattedData = [formattedData];
    }

    formattedData = formattedData.map((set) => {
      set = set
        .map((item) => formatItem(item))
        .sort(function (a, b) {
          return String(a.vintage).localeCompare(String(b.vintage));
        });

      return set;
    });
    let setNames = [];
    let wines = [];
    formattedData.forEach((set) => {
      if (set && set.length > 0) {
        set.sort(function (a, b) {
          return String(a.name).localeCompare(String(b.name));
        });
        setNames.push(...set.map((item) => item.name));
        wines.push(set[0].wine);
      }
    });
    setNames = [...new Set(setNames)].sort();
    formattedData.forEach((set, setIndex) => {
      setNames.forEach((name, index) => {
        if (!set[index] || set[index].name !== name) {
          set.splice(index, 0, {
            name,
            wine: wines[setIndex],
            facing: 0,
            minPrice: { icon: 'down', value: 0 },
            value: { icon: 'neutral', value: 0 },
            maxPrice: { icon: 'up', value: 0 }
          });
        }
      });
      const averagePrice = getAverage(
        set.filter((item) => item.value && item.value.value)
      );
      const averageMaxPrice = getMaxValue(set);
      const averageMinPrice = getMinValue(set);

      if (set.length > 0) {
        set.unshift({
          name: I18n.t('placeholderAll'),
          vintage: I18n.t('placeholderAll'),
          wine: { name: wines[setIndex] },
          minPrice: { icon: 'down', value: averageMinPrice },
          value: { icon: 'neutral', value: averagePrice },
          maxPrice: { icon: 'up', value: averageMaxPrice }
        });
      }
    });

    return formattedData;
  };

  const getAverage = (dataArray = []) => {
    if (dataArray && dataArray.length > 0) {
      const sumFacing = dataArray
        .filter((item) => item.facing)
        .map((item) => item.facing)
        .reduce((value, acc) => value + acc, 0);
      const sumValueFacing = dataArray
        .filter((item) => item.facing && item.value.value)
        .map((item) => item.value.value * item.facing)
        .reduce((value, acc) => value + acc);
      return sumValueFacing / sumFacing;
    }

    return 0;
  };

  const getMaxValue = (dataArray) => {
    return dataArray.length > 0
      ? Math.max(
          ...dataArray
            .filter((item) => item.maxPrice.value)
            .map((item) => item.maxPrice.value)
        )
      : 0;
  };

  const getMinValue = (dataArray) => {
    return dataArray.length > 0
      ? Math.min(
          ...dataArray
            .filter((item) => item.minPrice.value)
            .map((item) => item.minPrice.value)
        )
      : 0;
  };

  const formatItem = (item) => {
    return {
      ...item,
      name: item.vintage,
      minPrice: { icon: 'down', value: item.min_price },
      value: { icon: 'neutral', value: item.median_price },
      maxPrice: { icon: 'up', value: item.max_price }
    };
  };

  const getRivals = () => {
    const rival_wines = data?.rival_wines || [];
    const client_wines = data?.client_wines || [];
    if (!client_wines.length) {
      return [];
    }
    return [
      ...new Set(
        rival_wines.filter(
          (item) => wine && item.client_wines.includes(wine.label)
        )
      )
    ];
  };

  const renderTooltip = (tooltipData, labels) => {
    return (
      <div className="rhcTooltip">
        <div className="rhcTooltipContainer">
          <div className="rhcTooltipValues">
            <p className="rhcLabel">{I18n.t('lowPrice')}</p>
            <p className="rhcLabel">{I18n.t('medianPrice')}</p>
            <p className="rhcLabel">{I18n.t('highPrice')}</p>
          </div>
        </div>
        {tooltipData
          .filter(
            (set, index) => set && labels[index] && set.value && set.value.value
          )
          .map((set, index) => (
            <React.Fragment key={index}>
              <div className="rhcTooltipContainer">
                <p>{`${set.wine.name ? set.wine.name : set.wine} ${
                  set.name
                }`}</p>
                <div className="rhcTooltipValues">
                  {Object.keys(set).map(
                    (key) =>
                      set[key] &&
                      set[key].value && (
                        <div key={key}>
                          <EvolutionIcon icon={set[key].icon} />
                          <p>
                            {set[key].value.toLocaleString('fr-FR', {
                              style: 'currency',
                              currency: set.displayed_currency
                                ? set.displayed_currency
                                : 'EUR',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 2
                            })}
                          </p>
                        </div>
                      )
                  )}
                </div>
              </div>
            </React.Fragment>
          ))}
      </div>
    );
  };

  const getWineOptions = () => {
    const { client_wines = [] } = data || {};
    if (isBranded) {
      return [...new Set(getWineOptionsFromBrands())].map((item) => ({
        label: item,
        value: item
      }));
    }
    return [...new Set(client_wines.map((item) => item.wine))].map((item) => ({
      label: item,
      value: item
    }));
  };

  const getLabels = (chartData) => {
    let labels = [];
    if (chartData && chartData.length > 0) {
      labels = [
        ...new Set(
          chartData.map((set) =>
            set && set.length > 0 ? get(set, '[1].wine') : ''
          )
        )
      ];
    }
    if (labels.length > 0 && activeRadio === 'year') {
      labels.push(`${labels[0]} n-1`);
    }
    return labels;
  };
  const { competitors } = form;
  const rivals = getRivals();
  const dataVintageChart = formatVintageChart();

  const handleExportOptions = isBranded
    ? {
        brands: formatParam(JSON.stringify(map(get(form, 'wine'), 'label'))),
        rivals: formatParam(JSON.stringify(competitors)),
        wine_type: form.wineType
      }
    : {
        wines: formatParam(JSON.stringify(map(get(form, 'wine'), 'label'))),
        rivals: formatParam(JSON.stringify(competitors)),
        wine_type: form.wineType
      };
  const labels = getLabels(dataVintageChart);

  return (
    <React.Fragment>
      <PriceContainer>
        <Card id="prix-moyen-millesime">
          <CardHeader
            alignItems="center"
            title={I18n.t('restaurants&caves.price.byVintageTitle')}
            styles={`.cardHeaderRight{width:100%;margin-top:10px;};flex-direction:column;align-items:flex-start;`}
            fontSize="20px"
            iconRight="right"
            rightComponents={
              <div className="rightComponentsContainer ignored-export">
                <div className="headerInputsContainer">
                  <ChartInputsBloc
                    wine={wine}
                    vintageEnd={vintageEnd}
                    vintageStart={vintageStart}
                    wineOptions={getWineOptions()}
                    vintageOptions={[
                      { value: null, label: I18n.t('placeholderAll') },
                      ...getVintageOptions()
                    ]}
                    onChange={(field, value) => handleChangeChart(field, value)}
                    bigOptions={isBranded}
                  />
                  <ExportButton
                    name={`${I18n.t(
                      'restaurants&caves.price.byVintageTitle'
                    )}-${get(form, 'currentCampaign.market.name')}-${join(
                      map(get(form, 'wine'), 'label'),
                      '-'
                    )}`}
                    jpg
                    pdf
                    xls
                    targetElement="#prix-moyen-millesime"
                    path={`/distribution/${campaign?.id}/price/xls?`}
                    options={handleExportOptions}
                    newApi
                  />
                </div>
              </div>
            }
          />
          <BarGraph
            data={dataVintageChart}
            height={380}
            renderTooltip={(props) => renderTooltip(props, labels)}
            tooltipOffset={{ x: 5, y: -15 }}
            labels={labels}
            yAxisProps={{
              tickFormatter: (value) =>
                value.toLocaleString('fr-FR', {
                  style: 'currency',
                  currency: get(
                    dataVintageChart,
                    '[0][1].displayed_currency',
                    'EUR'
                  ),
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                })
            }}
            colors={['#e25d34', '#1f2b37']}
          />
          <div className="selectSetsContainer ignored-export">
            <ChartCompareBloc
              competitorName={competitorName}
              compare={[
                {
                  value: 'none',
                  label: I18n.t('restaurants&caves.price.noCompare')
                },
                {
                  value: 'year',
                  label: I18n.t('restaurants&caves.price.compareLastYear')
                },
                {
                  value: 'rival',
                  label: I18n.t('restaurants&caves.price.compareRival')
                }
              ].map((item) => ({
                ...item,
                checked: item.value === activeRadio
              }))}
              competitorNames={
                rivals && rivals.length > 0
                  ? [...new Set(rivals.map((item) => item.wine))].map(
                      (item) => ({
                        value: item,
                        label: item
                      })
                    )
                  : []
              }
              onChange={(field, value) => handleChangeChart(field, value)}
              handleRadioChange={(value) =>
                setState((prev) => ({ ...prev, activeRadio: value }))
              }
            />
          </div>
        </Card>
        <PriceWineCard
          isBranded={isBranded}
          campaignId={campaign?.id}
          priceData={data}
          formData={form}
          client_marques={client_marques}
          client_wines={client_wines}
        />
      </PriceContainer>
      <Spinner loading={isLoading} />
    </React.Fragment>
  );
};

export default Price;
